<div class="ticket-detail">
  <!-- Header -->
  <div class="detail-header">
    <button class="back-btn" (click)="onBack()" title="Back to queue">
      <i class="fa-solid fa-arrow-left"></i>
    </button>
    <div class="header-info" *ngIf="issue">
      <i class="fa-solid fa-ticket"></i>
      <div class="header-text">
        <h1>
          <span class="detail-number">{{ ticketLabel }}</span> {{ issue.Title }}
          <!-- Derived from the linked messages — same signal the Messages screen badges. -->
          <span class="escalated-chip" *ngIf="isEscalated" title="An escalated message on this ticket is waiting for human review">
            <i class="fa-solid fa-triangle-exclamation"></i> Escalated
          </span>
        </h1>
        <p>{{ type?.Name || issue.IssueType || 'Ticket' }} · {{ status?.Name || issue.Status }}</p>
      </div>
    </div>
    <button class="escalate-btn" *ngIf="issue && !isLoading && canEscalate" (click)="openEscalatePanel()"
            title="Escalate this ticket for senior/human attention">
      <i class="fa-solid fa-triangle-exclamation"></i> Escalate
    </button>
    <button class="merge-btn" *ngIf="issue && !isLoading" (click)="openMergePanel()" title="Merge this ticket into another">
      <i class="fa-solid fa-code-merge"></i> Merge
    </button>
    <button class="merge-btn" *ngIf="issue && !isLoading" (click)="openExportPanel()" title="Export this ticket as a PDF">
      <i class="fa-solid fa-file-pdf"></i> Export
    </button>
  </div>

  <!-- Export PDF panel: internal-notes choice + confirm (mirrors the escalate panel pattern) -->
  <div class="merge-panel" *ngIf="showExportPanel">
    <div class="merge-panel-head">
      <strong>Export {{ ticketLabel }} as PDF</strong>
      <button class="merge-close" (click)="closeExportPanel()"><i class="fa-solid fa-xmark"></i></button>
    </div>
    <p class="merge-hint">
      Downloads the ticket's properties and full conversation as a PDF. Internal notes are
      left out unless you include them — exported files often leave Izzy.
    </p>
    <label class="export-notes-choice">
      <input type="checkbox" [(ngModel)]="exportIncludeInternalNotes" [disabled]="isExporting" />
      Include internal notes (labeled INTERNAL NOTE in the PDF)
    </label>
    <div class="merge-error" *ngIf="exportError"><i class="fa-solid fa-triangle-exclamation"></i> {{ exportError }}</div>
    <div class="merge-actions">
      <button class="merge-cancel" (click)="closeExportPanel()" [disabled]="isExporting">Cancel</button>
      <button class="merge-confirm" (click)="confirmExport()" [disabled]="isExporting">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="isExporting"></i>
        Export PDF
      </button>
    </div>
  </div>

  <!-- Escalate panel: optional reason + confirm (mirrors the merge panel pattern) -->
  <div class="merge-panel" *ngIf="showEscalatePanel">
    <div class="merge-panel-head">
      <strong>Escalate {{ ticketLabel }}</strong>
      <button class="merge-close" (click)="closeEscalatePanel()"><i class="fa-solid fa-xmark"></i></button>
    </div>
    <p class="merge-hint">
      Marks this ticket as needing human attention: it moves to In Progress, routes to the
      escalation team when one is configured, and your reason is recorded as an internal note.
    </p>
    <textarea class="escalate-reason" rows="2" [(ngModel)]="escalateReason"
              placeholder="Why are you escalating? (optional — goes in the internal note)"
              [disabled]="isEscalating"></textarea>
    <div class="merge-error" *ngIf="escalateError"><i class="fa-solid fa-triangle-exclamation"></i> {{ escalateError }}</div>
    <div class="merge-actions">
      <button class="merge-cancel" (click)="closeEscalatePanel()" [disabled]="isEscalating">Cancel</button>
      <button class="merge-confirm" (click)="confirmEscalate()" [disabled]="isEscalating">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="isEscalating"></i>
        Escalate ticket
      </button>
    </div>
  </div>

  <!-- Collision detection (§11.5): other agents currently viewing this ticket -->
  <div class="presence-banner" *ngIf="otherViewers.length > 0">
    <i class="fa-solid fa-eye"></i>
    <span *ngIf="otherViewers.length === 1">{{ otherViewers[0] }} is also viewing this ticket.</span>
    <span *ngIf="otherViewers.length > 1">{{ otherViewers.length }} others are also viewing this ticket: {{ otherViewers.join(', ') }}</span>
  </div>

  <!-- Merge panel: pick the survivor ticket this one will be merged INTO -->
  <div class="merge-panel" *ngIf="showMergePanel">
    <div class="merge-panel-head">
      <strong>Merge {{ ticketLabel }} into…</strong>
      <button class="merge-close" (click)="closeMergePanel()"><i class="fa-solid fa-xmark"></i></button>
    </div>
    <p class="merge-hint">This ticket's conversation moves into the ticket you choose, and this one closes as merged.</p>
    <div class="merge-search-row">
      <input type="text" class="merge-search" placeholder="Search by ticket # or title…"
             [(ngModel)]="mergeSearch" (keyup.enter)="searchMergeCandidates()" />
      <button class="merge-search-btn" (click)="searchMergeCandidates()">Search</button>
    </div>
    <ul class="merge-candidates" *ngIf="mergeCandidates.length > 0">
      <li *ngFor="let c of mergeCandidates"
          [class.selected]="selectedSurvivor?.id === c.id"
          (click)="selectSurvivor(c)">
        <span class="cand-number">{{ c.issueNumber }}</span>
        <span class="cand-title">{{ c.title }}</span>
      </li>
    </ul>
    <div class="merge-error" *ngIf="mergeError"><i class="fa-solid fa-triangle-exclamation"></i> {{ mergeError }}</div>
    <div class="merge-actions">
      <button class="merge-cancel" (click)="closeMergePanel()" [disabled]="isMerging">Cancel</button>
      <button class="merge-confirm" (click)="confirmMerge()" [disabled]="!selectedSurvivor || isMerging">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="isMerging"></i>
        Merge into {{ selectedSurvivor?.issueNumber || '…' }}
      </button>
    </div>
  </div>

  <!-- Reporter-merge prompt: shown after a ticket merge when the two reporters differ -->
  <div class="merge-panel reporter-merge" *ngIf="showReporterMergePrompt && reporterMerge">
    <div class="merge-panel-head">
      <strong>Same person?</strong>
    </div>
    <p class="merge-hint">
      These tickets came from two different contacts — <em>{{ reporterMerge.survivorName }}</em> and
      <em>{{ reporterMerge.loserName }}</em>. If they're the same person, you can combine them into a
      single contact so their history stays together. <em>{{ reporterMerge.survivorName }}</em> will be kept.
    </p>
    <div class="merge-error" *ngIf="mergeError"><i class="fa-solid fa-triangle-exclamation"></i> {{ mergeError }}</div>
    <div class="merge-actions">
      <button class="merge-cancel" (click)="skipReporterMerge()" [disabled]="isMerging">No, keep separate</button>
      <button class="merge-confirm" (click)="confirmReporterMerge()" [disabled]="isMerging">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="isMerging"></i>
        Yes, merge into {{ reporterMerge.survivorName }}
      </button>
    </div>
  </div>

  <!-- Follow-up suggestion banner: this ticket may belong to another (staff confirm/dismiss) -->
  <div class="suggestion-banner" *ngIf="suggestion && suggestionInfo && !showReporterMergePrompt">
    <div class="suggestion-text">
      <i class="fa-solid fa-link"></i>
      <span>
        This may be a follow-up to <strong>{{ suggestionInfo.issueNumber }}</strong> from the same person.
        <span class="suggestion-reason">{{ suggestionInfo.reason }}</span>
      </span>
    </div>
    <div class="merge-error" *ngIf="mergeError"><i class="fa-solid fa-triangle-exclamation"></i> {{ mergeError }}</div>
    <div class="suggestion-actions">
      <button class="suggestion-dismiss" (click)="dismissSuggestion()" [disabled]="isResolvingSuggestion">Keep separate</button>
      <button class="suggestion-merge" (click)="acceptSuggestion()" [disabled]="isResolvingSuggestion">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="isResolvingSuggestion"></i>
        Merge into {{ suggestionInfo.issueNumber }}
      </button>
    </div>
  </div>

  <div class="detail-error" *ngIf="errorMessage">
    <i class="fa-solid fa-triangle-exclamation"></i> {{ errorMessage }}
  </div>

  <div class="detail-loading" *ngIf="isLoading">
    <i class="fa-solid fa-spinner fa-spin"></i> Loading ticket…
  </div>

  <div class="detail-body" #detailBody [class.dragging]="isDraggingSplitter" *ngIf="!isLoading && issue">
    <!-- Unified cross-channel timeline -->
    <div class="timeline-pane">
      <div class="timeline-card">
        <div class="card-header">
          <h3><i class="fa-solid fa-timeline"></i> Conversation</h3>
        </div>
        <div class="timeline-list">
          <div class="timeline-empty" *ngIf="timeline.length === 0">
            <i class="fa-solid fa-inbox"></i>
            <p>No messages on this ticket yet.</p>
          </div>

          <div class="timeline-entry"
               *ngFor="let e of timeline"
               [class.is-reply]="e.kind === 'reply'"
               [class.is-note]="e.kind === 'note'"
               [class.is-comment]="e.kind === 'comment'">
            <div class="entry-rail">
              <span class="entry-icon"><i [class]="getEntryIcon(e)"></i></span>
            </div>
            <div class="entry-card">
              <div class="entry-meta">
                <span class="entry-who">{{ e.who }}</span>
                <span class="entry-channel" *ngIf="e.channelType || e.channelName">{{ e.channelType || e.channelName }}</span>
                <span class="entry-forward-badge" *ngIf="e.outboundType === 'Forward'"><i class="fa-solid fa-share"></i> Forwarded</span>
                <span class="entry-reply-badge" [ngClass]="getReplyBadgeClass(e.approvalStatus)" *ngIf="e.kind === 'reply' && e.outboundType !== 'Forward'">{{ e.approvalStatus || 'Reply' }}</span>
                <span class="entry-note-badge" *ngIf="e.kind === 'note'">Internal</span>
                @if (e.noteOnMessageAt) {
                  <span class="entry-note-on" [title]="'Written on the message received ' + (e.noteOnMessageAt | date:'MMM d, y, h:mm a')">on message from {{ e.noteOnMessageAt | date:'MMM d, h:mm a' }}</span>
                }
                <span class="entry-when">{{ e.when | date:'MMM d, y, h:mm a' }}</span>
              </div>
              <!-- Outbound audit: exactly who this reply/forward was delivered to. -->
              <div class="entry-recipients" *ngIf="e.sentRecipients">{{ e.sentRecipients }}</div>
              <!-- Read-only reply content (hidden while editing this reply). -->
              <div class="entry-content" [innerHTML]="e.content" *ngIf="editingMessageId !== e.messageId"></div>

              <!-- Inline edit of the AI draft — same Quill editor the Review & Approve screen uses. -->
              <div class="entry-edit" *ngIf="canApproveReply(e) && editingMessageId === e.messageId">
                <izzy-message-editor
                  [originalContent]="editingOriginalContent"
                  [currentContent]="editedReplyContent"
                  [messageFormat]="'HTML'"
                  [assistChannelId]="editingChannelId"
                  [assistChannelMessageId]="editingMessageId"
                  [assistThreadContext]="editingThreadContext"
                  (save)="onTicketEditorSave(e.messageId, $event)"
                  (saveDraft)="onTicketEditorSaveDraft(e.messageId, $event)"
                  (cancel)="cancelEditReply()">
                </izzy-message-editor>
              </div>

              <!-- Approve / Edit / Reject the pending AI reply in-place (same path as Review & Approve). -->
              <div class="entry-approve" *ngIf="canApproveReply(e) && editingMessageId !== e.messageId">
                <button type="button" class="approve-btn"
                        (click)="approveReply(e.messageId)"
                        [disabled]="approvingMessageId === e.messageId">
                  <i class="fa-solid" [class.fa-spinner]="approvingMessageId === e.messageId" [class.fa-spin]="approvingMessageId === e.messageId" [class.fa-check]="approvingMessageId !== e.messageId"></i>
                  Approve &amp; Send
                </button>
                <button type="button" class="edit-btn" (click)="startEditReply(e)" [disabled]="approvingMessageId === e.messageId">
                  <i class="fa-solid fa-pen"></i> Edit
                </button>
                <button type="button" class="reject-btn"
                        (click)="rejectReply(e.messageId)"
                        [disabled]="approvingMessageId === e.messageId">
                  <i class="fa-solid fa-xmark"></i> Reject
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Composer: add an internal note or a customer-facing comment (parent persists). -->
        <div class="comment-composer">
          <div class="composer-toggle">
            <button type="button" class="composer-tab" [class.active]="commentSource === 'internal'" (click)="setCommentSource('internal')">
              <i class="fa-solid fa-lock"></i> Internal note
            </button>
            <button type="button" class="composer-tab" [class.active]="commentSource === 'outbound'" (click)="setCommentSource('outbound')">
              <i class="fa-solid fa-reply"></i> Reply
            </button>
            <!-- Macros (§11.6): insert a canned reply (placeholders resolved, field changes applied). -->
            <div class="macro-menu" *ngIf="macros.length">
              <button type="button" class="composer-tab macro-trigger" (click)="toggleMacroMenu()">
                <i class="fa-solid fa-bolt-lightning"></i> Macros <i class="fa-solid fa-caret-down"></i>
              </button>
              <div class="macro-dropdown" *ngIf="showMacroMenu">
                <input type="text" class="macro-search" [(ngModel)]="macroSearch" placeholder="Search macros…" (click)="$event.stopPropagation()" />
                <button type="button" class="macro-item" *ngFor="let m of filteredMacros()" (click)="applyMacro(m)">
                  <span class="macro-item-name">{{ m.Name }}</span>
                  <span class="macro-item-meta" *ngIf="m.FieldChanges">sets fields</span>
                </button>
                <div class="macro-empty" *ngIf="filteredMacros().length === 0">No matching macros</div>
              </div>
            </div>
          </div>
          <!-- Outbound recipients: Reply / Reply All + editable Cc. Server applies suppression + cap. -->
          <div class="composer-recipients" *ngIf="commentSource === 'outbound'">
            <div class="recipients-mode" *ngIf="replyAllAvailable">
              <button type="button" class="mode-button" [class.active]="!replyAll"
                      (click)="setComposerReplyMode(false)" title="Reply to the customer only">
                <i class="fa-solid fa-reply"></i> Reply
              </button>
              <button type="button" class="mode-button" [class.active]="replyAll"
                      (click)="setComposerReplyMode(true)" title="Reply to everyone on the original email">
                <i class="fa-solid fa-reply-all"></i> Reply All
              </button>
            </div>
            <div class="recipients-cc">
              <label>Cc:</label>
              <input type="text" [(ngModel)]="ccInput" [disabled]="isAddingComment"
                     placeholder="Add addresses, separated by commas" />
            </div>
          </div>
          <!-- Reply: the same rich editor as Review & Approve (HTML body, AI assistant scoped to
               the ticket's channel). Internal notes stay plain text. -->
          @if (commentSource === 'outbound') {
            <izzy-message-editor #replyEditor class="composer-editor"
              [compact]="true"
              [currentContent]="replyBodySeed"
              [readOnly]="isAddingComment"
              placeholder="Write a reply to send to the customer on this ticket…"
              [assistChannelId]="composerChannelId"
              [assistThreadContext]="composerThreadContext"
              (contentChange)="replyBody = $event.content">
            </izzy-message-editor>
          } @else {
            <textarea class="composer-input"
                      [(ngModel)]="commentBody"
                      placeholder="Add an internal note (staff-only, never sent to the customer)…"
                      rows="3"
                      [disabled]="isAddingComment"></textarea>
          }
          <div class="composer-error" *ngIf="commentError"><i class="fa-solid fa-triangle-exclamation"></i> {{ commentError }}</div>
          <div class="composer-actions">
            <span class="composer-hint" *ngIf="commentSource === 'outbound'">Sends to the customer on the ticket's channel. Cc above also applies when you approve the AI draft.</span>
            <button class="composer-send" (click)="addComment()" [disabled]="!composerHasBody() || isAddingComment">
              <i class="fa-solid fa-spinner fa-spin" *ngIf="isAddingComment"></i>
              {{ commentSource === 'internal' ? 'Add note' : (isAddingComment ? 'Sending…' : 'Send reply') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Drag to resize the properties pane (mirrors message-editor splitter) -->
    <div class="splitter" [class.dragging]="isDraggingSplitter" (mousedown)="startSplitterDrag($event)" title="Drag to resize">
      <span class="splitter-handle"><i class="fa-solid fa-grip-lines-vertical"></i></span>
    </div>

    <!--
      Right pane. Tabbed so an agent can work the whole ticket without leaving it: the ticket's
      properties, its lifecycle history, and the AI analysis behind the reply they're approving.
    -->
    <aside class="properties-pane" [style.width.%]="propsWidthPct">
      <div class="props-card">
        <div class="pane-tab-bar">
          <button type="button" class="pane-tab" [class.active]="activeTab === 'properties'" (click)="setTab('properties')">
            <i class="fa-solid fa-circle-info"></i><span>Properties</span>
          </button>
          <button type="button" class="pane-tab" [class.active]="activeTab === 'timeline'" (click)="setTab('timeline')">
            <i class="fa-solid fa-clock-rotate-left"></i><span>Timeline</span>
          </button>
          <button type="button" class="pane-tab" [class.active]="activeTab === 'analysis'" (click)="setTab('analysis')">
            <i class="fa-solid fa-brain"></i><span>AI Analysis</span>
          </button>
        </div>

        <dl class="props" *ngIf="activeTab === 'properties'">
        <dt>Ticket</dt>
        <dd class="prop-number">{{ ticketLabel }}</dd>

        <dt>Status</dt>
        <dd class="prop-status">
          <span class="status-chip" [style.background]="getStatusColor()">{{ status?.Name || issue.Status || 'Unknown' }}</span>
          <select
            class="status-select"
            [ngModel]="issue.StatusID"
            (ngModelChange)="changeStatus($event)"
            [disabled]="isChangingStatus">
            <option *ngFor="let s of statuses" [value]="s.ID">{{ s.Name }}</option>
          </select>
        </dd>

        <dt>Type</dt>
        <dd>{{ issue.IssueType || '—' }}</dd>

        <dt>Priority</dt>
        <dd class="prop-priority">
          <span class="priority-dot" [ngClass]="getPriorityClass()"></span>
          {{ issue.Priority || '—' }}
        </dd>

        <dt>Reporter</dt>
        <dd>{{ issue.ReporterEmail || '—' }}</dd>

        <dt>Assignee</dt>
        <dd class="assignee-cell">
          <select class="assignee-select"
                  [ngModel]="issue.AssigneeRecordID || ''"
                  (ngModelChange)="changeAssignee($event)"
                  [disabled]="isAssigning">
            <option value="">Unassigned</option>
            <!-- The acting user, when currently assigned, so the dropdown reflects reality (they self-assign via the button). -->
            <option *ngIf="isAssignedToMe" [value]="actingPid">{{ assigneeName }} (you)</option>
            <option *ngFor="let p of assignablePeople" [value]="p.id">{{ p.name }}</option>
          </select>
          <button type="button" class="assign-me-btn"
                  *ngIf="actingPid && !isAssignedToMe"
                  (click)="assignToMe()" [disabled]="isAssigning"
                  title="Assign this ticket to yourself">
            <i class="fa-solid fa-user-check"></i> Assign to me
          </button>
          <span class="assigned-me-tag" *ngIf="isAssignedToMe"><i class="fa-solid fa-check"></i> Assigned to you</span>
        </dd>

        <dt>SLA</dt>
        <dd>
          <span class="sla-badge" [ngClass]="'sla-' + slaKind" *ngIf="slaKind !== 'none'">{{ slaLabel }}</span>
          <span class="sla-none" *ngIf="slaKind === 'none'">No SLA</span>
        </dd>

        <dt>Created</dt>
        <dd>{{ issue.__mj_CreatedAt | date:'MMM d, y, h:mm a' }}</dd>
        </dl>

        <!--
          Timeline: the ticket's lifecycle — status/priority/assignee transitions, SLA clock events,
          and one compact line per message. Bodies stay in the Conversation pane, so this reads as an
          audit trail rather than a second transcript.
        -->
        <div class="activity-pane" *ngIf="activeTab === 'timeline'">
          <div class="activity-loading" *ngIf="isActivityLoading">
            <i class="fa-solid fa-spinner fa-spin"></i> Loading history…
          </div>
          <div class="activity-error" *ngIf="activityError">
            <i class="fa-solid fa-triangle-exclamation"></i> {{ activityError }}
          </div>
          <div class="activity-empty" *ngIf="!isActivityLoading && !activityError && activity.length === 0">
            <i class="fa-solid fa-clock-rotate-left"></i>
            <p>No history recorded for this ticket yet.</p>
          </div>
          <ul class="activity-list" *ngIf="!isActivityLoading && activity.length > 0">
            <li class="activity-item" *ngFor="let a of activity" [ngClass]="'activity-' + a.kind">
              <span class="activity-icon"><i [class]="a.icon"></i></span>
              <div class="activity-body">
                <div class="activity-label">{{ a.label }}</div>
                <div class="activity-detail" *ngIf="a.detail">{{ a.detail }}</div>
                <div class="activity-when">{{ a.when | date:'MMM d, y, h:mm a' }}</div>
              </div>
            </li>
          </ul>
        </div>

        <!--
          AI Analysis: the same pane the Review & Approve screen shows, via <izzy-ai-analysis>.
          Scoped to one message because the score, rubric reasoning and channel threshold live on
          ChannelMessage — an Issue has none of its own — so the agent picks which reply to inspect.
        -->
        <div class="analysis-pane" *ngIf="activeTab === 'analysis'">
          <div class="analysis-empty" *ngIf="analysisMessages.length === 0">
            <i class="fa-solid fa-chart-simple"></i>
            <p>No AI analysis on this ticket yet. Scores appear once Izzy drafts a reply.</p>
          </div>

          <ng-container *ngIf="analysisMessages.length > 0">
            <div class="analysis-picker" *ngIf="analysisMessages.length > 1">
              <label for="analysisMsg">Analysing</label>
              <select id="analysisMsg" class="analysis-select" [(ngModel)]="selectedAnalysisMessageId">
                <option *ngFor="let m of analysisMessages" [value]="m.ID">{{ analysisMessageLabel(m) }}</option>
              </select>
            </div>

            <izzy-ai-analysis
              [message]="selectedAnalysisMessage"
              [isRescoring]="isRescoring"
              [canRescore]="canRescoreSelected"
              [isReprocessing]="isReprocessing"
              [organizationId]="organizationId"
              (rescoreRequested)="onRescoreRequested()">
            </izzy-ai-analysis>
          </ng-container>
        </div>
      </div>
    </aside>
  </div>
</div>
